import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Save, X, Tag, ChevronDown, Edit3, Youtube, Video, Link, AlertCircle, Tv, CheckSquare, Square, Upload, Radio, Play, Film, Loader2, ClipboardList } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import ImagePicker from './ImagePicker'

// Short-description teaser from a (YouTube) description: the FIRST PARAGRAPH (up to the first
// blank line), trimmed. Keeps short meaningfully shorter than the full long description while
// giving cards a natural teaser (replaces the old hard 200-char chop). vod_catalog stores the
// full text (TEXT column); the Roku search feed independently truncates to 200 at delivery
// (tbtv-watch handleRokuSearchFeed), so a longer stored short is safe. A generous word-boundary
// safety cap guards against a single giant paragraph with no blank line.
function shortDescFrom(desc) {
  const text = (desc || '').trim();
  if (!text) return '';
  let para = text.split(/\n\s*\n/)[0].trim(); // first paragraph
  const CAP = 500;
  if (para.length > CAP) {
    const slice = para.slice(0, CAP);
    const lastSpace = slice.lastIndexOf(' ');
    para = (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim() + '…';
  }
  return para;
}

// Parse videos from the /videos API response
function parseVideoList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data.result)) return data.result
  if (data.result?.videos && Array.isArray(data.result.videos)) return data.result.videos
  if (data.videos && Array.isArray(data.videos)) return data.videos
  if (data.data && Array.isArray(data.data)) return data.data
  return []
}

// ─── Strimm partner helpers ───────────────────────────────
function getStoredStrimPartners() {
  try {
    const raw = localStorage.getItem('strimm-partners')
    return raw ? JSON.parse(raw) : [{ id: 'musicfromthe412', channelId: '58789', label: 'Music From The 412' }]
  } catch { return [] }
}

// Parse Strimm duration — handles "HH:MM:SS", "MM:SS", numeric seconds, or numeric string
function parseStrimDuration(dur) {
  if (!dur) return 0
  // If it's already a number
  if (typeof dur === 'number') return Math.floor(dur)
  // If it's a pure numeric string (seconds)
  const numVal = Number(dur)
  if (!isNaN(numVal) && !dur.includes(':')) return Math.floor(numVal)
  // Colon-separated time string
  const parts = dur.split(':').map(Number)
  if (parts.some(isNaN)) return 0
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return 0
}

export default function CatalogCreate({ allTags, onComplete, onCancel, setStatus }) {
  const { api } = useAuth()
  const [searchParams] = useSearchParams()
  const youtubeIdParam = searchParams.get('youtube_id')
  const autoPopulatedRef = useRef(false)

  // VOD fields
  const [title, setTitle] = useState('')
  const [mediaUrl, setMediaUrl] = useState('')
  const [videoType, setVideoType] = useState('CF')
  const [quality, setQuality] = useState('FHD')
  const [duration, setDuration] = useState(0)
  const [shortDesc, setShortDesc] = useState('')
  const [longDesc, setLongDesc] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [releaseDate, setReleaseDate] = useState('')
  const [itemStatus, setItemStatus] = useState('draft')
  
  // Tags
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [loadingTags, setLoadingTags] = useState(false)
  
  // YouTube Archive selection
  const [ytVideos, setYtVideos] = useState([])
  const [ytSearch, setYtSearch] = useState('')
  const [ytLoading, setYtLoading] = useState(false)
  const [showYtDropdown, setShowYtDropdown] = useState(false)
  const [selectedYt, setSelectedYt] = useState(null)
  const ytDropdownRef = useRef(null)
  const ytSearchTimerRef = useRef(null)
  
  // CF Stream selection
  const [cfStreams, setCfStreams] = useState([])
  const [cfSearch, setCfSearch] = useState('')
  const [cfLoading, setCfLoading] = useState(false)
  const [showCfDropdown, setShowCfDropdown] = useState(false)
  const [selectedCf, setSelectedCf] = useState(null)
  const cfDropdownRef = useRef(null)
  const cfSearchTimerRef = useRef(null)

  // Mux Stream selection
  const [muxAssets, setMuxAssets] = useState([])
  const [muxSearch, setMuxSearch] = useState('')
  const [muxLoading, setMuxLoading] = useState(false)
  const [showMuxDropdown, setShowMuxDropdown] = useState(false)
  const [selectedMux, setSelectedMux] = useState(null)
  const muxDropdownRef = useRef(null)

  // ─── Strimm Partners selection ────────────────────────────
  const [strimPartners] = useState(getStoredStrimPartners)
  const [strimActivePartner, setStrimActivePartner] = useState(null)
  const [strimDate, setStrimDate] = useState(() => new Date().toISOString().split('T')[0])
  const [strimVideos, setStrimVideos] = useState([])
  const [strimSearch, setStrimSearch] = useState('')
  const [strimLoading, setStrimLoading] = useState(false)
  const [showStrimDropdown, setShowStrimDropdown] = useState(false)
  const [selectedStrim, setSelectedStrim] = useState(null)
  const strimDropdownRef = useRef(null)

  // Strimm multi-select batch import
  const [strimChecked, setStrimChecked] = useState(new Set())
  const [strimBatchMode, setStrimBatchMode] = useState(false)
  const [strimImporting, setStrimImporting] = useState(false)
  const [strimImportStatus, setStrimImportStatus] = useState('')
  const [strimBatchThumb, setStrimBatchThumb] = useState('')
  const [strimBatchTags, setStrimBatchTags] = useState(['tbtv-strimm'])
  const [strimBatchTagInput, setStrimBatchTagInput] = useState('')
  
  // Unified content library: event mode + live fields
  const [eventMode, setEventMode] = useState('ondemand')   // 'live' | 'ondemand'
  const [scheduledStart, setScheduledStart] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(120)
  const [cfLiveInputId, setCfLiveInputId] = useState('')
  const [viloudChannelName, setViloudChannelName] = useState('')
  const [viloudChannelId, setViloudChannelId] = useState('')
  const [streamNotes, setStreamNotes] = useState('')
  const [includeInSchedule, setIncludeInSchedule] = useState(true)
  const [liveInputs, setLiveInputs] = useState([])
  const [tbtvChannels, setTbtvChannels] = useState([])

  // Editing states for pre-populated fields
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingDate, setEditingDate] = useState(false)
  const [editingShortDesc, setEditingShortDesc] = useState(false)
  const [editingLongDesc, setEditingLongDesc] = useState(false)

  const [saving, setSaving] = useState(false)

  // Existing catalog titles for duplicate detection
  const [existingTitles, setExistingTitles] = useState(new Set())

  // Load initial data
  useEffect(() => {
    loadYtVideos()
    loadCfStreams()
    loadMuxAssets()
    loadExistingTitles()
    loadLiveInputs()
    loadTbtvChannels()
  }, [])

  // Auto-populate from YouTube Archive URL param (?youtube_id=xxx)
  useEffect(() => {
    if (!youtubeIdParam || autoPopulatedRef.current) return
    autoPopulatedRef.current = true
    async function autoPopulate() {
      try {
        setStatus('Loading YouTube video data...')
        const data = await api(`/youtube/videos/${youtubeIdParam}`)
        const video = data.video || data
        if (!video || !video.youtube_id) {
          setStatus('YouTube video not found')
          return
        }

        // Directly populate all fields (same as selectYtVideo but avoids stale closures)
        setSelectedYt(video)
        setYtSearch(video.title || video.youtube_id)
        setTitle(video.title || '')
        setThumbnailUrl(video.thumbnail_url || '')
        setLongDesc(video.description || '')
        setShortDesc(shortDescFrom(video.description))
        if (video.duration_minutes) setDuration(video.duration_minutes * 60)

        // Release date — same priority chain as the picker-side autofill at
        // line ~395: scheduled_start (live) → actual_start (live) →
        // published_at (YouTube upload date — the correct release date for
        // VOD-sourced items like playlist-synced videos) → synced_at (last
        // resort, effectively "today").
        const ytDate = video.scheduled_start || video.actual_start || video.published_at || video.synced_at
        if (ytDate) setReleaseDate(new Date(ytDate).toISOString().split('T')[0])

        // Live event fields
        if (video.cf_live_input_id) setCfLiveInputId(video.cf_live_input_id)
        if (video.viloud_channel_name) setViloudChannelName(video.viloud_channel_name)
        if (video.viloud_channel_id) setViloudChannelId(video.viloud_channel_id)
        if (video.duration_minutes) setDurationMinutes(video.duration_minutes)
        if (video.stream_notes) setStreamNotes(video.stream_notes)
        if (video.scheduled_start) {
          const dt = new Date(video.scheduled_start)
          const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
          setScheduledStart(local)
        }

        // Auto-detect live/ondemand mode from event type
        if (['upcoming', 'live'].includes(video.event_type)) {
          setEventMode('live')
          setIncludeInSchedule(true)
        } else {
          // On-demand: set YouTube embed URL
          if (video.youtube_id) {
            setMediaUrl(`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`)
            setVideoType('YT')
          }
        }

        // Fetch and merge tags
        setLoadingTags(true)
        const ytTags = await fetchTagsForYtVideo(video)
        if (ytTags.length > 0) setTags(prev => [...new Set([...prev, ...ytTags])])
        setLoadingTags(false)

        setStatus('')
      } catch (e) {
        setStatus(`Error loading YouTube video: ${e.message}`)
      }
    }
    setTimeout(autoPopulate, 500)
  }, [youtubeIdParam])

  async function loadLiveInputs() {
    try {
      const data = await api('/live-inputs')
      setLiveInputs(data.liveInputs || [])
    } catch { /* non-critical */ }
  }

  async function loadTbtvChannels() {
    try {
      const data = await api('/channels')
      setTbtvChannels(data.channels || [])
    } catch { /* non-critical */ }
  }

  // Extract distinctive keywords from title for library picker search
  function getTitleSearchHint() {
    const skipWords = new Set(['the','a','an','on','in','at','vs','of','for','and','or',
      'live','from','to','night','day','week','season','regular'])
    const words = (title || '')
      .replace(/[–—\-:]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 1 && !skipWords.has(w.toLowerCase()))
    return words.slice(0, 4).join(' ')
  }

  function handleVideoTypeChange(newType) {
    const searchHint = getTitleSearchHint()
    setVideoType(newType)

    if (newType === 'YT') {
      // If already have a YT selection, use it; otherwise open picker
      if (selectedYt?.youtube_id) {
        setMediaUrl(`https://www.youtube.com/embed/${selectedYt.youtube_id}?autoplay=1&rel=0`)
      } else if (searchHint) {
        setYtSearch(searchHint)
        setShowYtDropdown(true)
        setStatus('Select a YouTube video from the archive')
      } else {
        setStatus('⚠ Select a YouTube video from the archive')
      }
    } else if (newType === 'MUX') {
      if (selectedMux) {
        const pid = selectedMux.playback_ids?.[0]?.id
        if (pid) setMediaUrl(`https://stream.mux.com/${pid}.m3u8`)
      } else if (searchHint) {
        setMuxSearch(searchHint)
        setShowMuxDropdown(true)
        setStatus('Select a Mux asset')
      } else {
        setStatus('⚠ Select a Mux asset')
      }
    } else if (newType === 'CF') {
      if (selectedCf?.uid) {
        setMediaUrl(selectedCf._hlsUrl || mediaUrl)
      } else if (searchHint) {
        setCfSearch(searchHint)
        setShowCfDropdown(true)
        setStatus('Select a Cloudflare Stream video')
      } else {
        setStatus('⚠ Select a Cloudflare Stream video')
      }
    }
    // MP4 and HLS: leave as-is, user enters URL manually
  }

  function handleTbtvChannelSelect(viloudId) {
    if (!viloudId) {
      setViloudChannelId('')
      setViloudChannelName('')
      return
    }
    const ch = tbtvChannels.find(c => c.viloud_id === viloudId)
    if (ch) {
      setViloudChannelId(ch.viloud_id || '')
      setViloudChannelName(ch.name || '')
      if (ch.cf_live_input_id) setCfLiveInputId(ch.cf_live_input_id)
    }
  }

  async function loadExistingTitles() {
    try {
      const data = await api('/vod')
      const titles = (data.items || []).map(i => (i.title || '').trim().toLowerCase())
      setExistingTitles(new Set(titles.filter(Boolean)))
    } catch { /* non-critical */ }
  }

  function isDuplicateTitle(t) {
    return existingTitles.has((t || '').trim().toLowerCase())
  }

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (ytDropdownRef.current && !ytDropdownRef.current.contains(e.target)) {
        setShowYtDropdown(false)
      }
      if (cfDropdownRef.current && !cfDropdownRef.current.contains(e.target)) {
        setShowCfDropdown(false)
      }
      if (muxDropdownRef.current && !muxDropdownRef.current.contains(e.target)) {
        setShowMuxDropdown(false)
      }
      // Don't auto-close strimm dropdown in batch mode
      if (!strimBatchMode && strimDropdownRef.current && !strimDropdownRef.current.contains(e.target)) {
        setShowStrimDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [strimBatchMode])

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (ytSearchTimerRef.current) clearTimeout(ytSearchTimerRef.current)
      if (cfSearchTimerRef.current) clearTimeout(cfSearchTimerRef.current)
    }
  }, [])

  // === YouTube Archive Functions ===
  async function loadYtVideos() {
    setYtLoading(true)
    try {
      const data = await api('/youtube/videos?limit=100&unlinked=true')
      setYtVideos(data.videos || [])
    } catch (e) {
      console.error('Failed to load YT videos:', e)
      setYtVideos([])
    }
    setYtLoading(false)
  }

  async function searchYtVideos(query) {
    if (!query.trim()) return
    setYtLoading(true)
    try {
      const data = await api(`/youtube/videos?search=${encodeURIComponent(query)}&limit=50`)
      setYtVideos(data.videos || [])
    } catch (e) {
      console.error('YT search failed:', e)
    }
    setYtLoading(false)
  }

  function handleYtSearchChange(e) {
    const val = e.target.value
    setYtSearch(val)
    setShowYtDropdown(true)
    
    if (ytSearchTimerRef.current) clearTimeout(ytSearchTimerRef.current)
    if (val.trim().length >= 2) {
      ytSearchTimerRef.current = setTimeout(() => searchYtVideos(val), 400)
    } else if (val.trim().length === 0) {
      loadYtVideos()
    }
  }

  function getDisplayedYtVideos() {
    if (!ytSearch.trim()) return ytVideos
    const q = ytSearch.toLowerCase()
    return ytVideos.filter(v =>
      (v.title || '').toLowerCase().includes(q) ||
      (v.youtube_id || '').toLowerCase().includes(q)
    )
  }

  async function selectYtVideo(video) {
    setSelectedYt(video)
    setYtSearch(video.title || video.youtube_id)
    setShowYtDropdown(false)

    // Pre-populate VOD fields from YouTube metadata
    if (!title || editingTitle === false) setTitle(video.title || '')
    if (!thumbnailUrl) setThumbnailUrl(video.thumbnail_url || '')
    if (!longDesc) setLongDesc(video.description || '')
    if (!shortDesc) setShortDesc(shortDescFrom(video.description))
    if (video.duration_minutes && !selectedCf) setDuration(video.duration_minutes * 60)

    // For on-demand: auto-set YouTube embed URL as media source if no other source selected
    if (eventMode === 'ondemand' && !selectedCf && !selectedMux && !selectedStrim && !mediaUrl && video.youtube_id) {
      setMediaUrl(`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`)
      setVideoType('YT')
    }
    
    // Set release date from YouTube.
    // Priority chain (dev24):
    //   1. scheduled_start   — live event's scheduled air time (existing
    //                          behavior for live-event catalog creation)
    //   2. actual_start      — when a live event actually went live
    //   3. published_at      — YouTube upload date (from video.snippet.publishedAt);
    //                          the correct release date for VOD-sourced items
    //                          (playlist-synced videos, completed channel VODs)
    //   4. synced_at         — last-resort fallback (when we synced the row)
    // Live-event flow behavior is preserved: scheduled_start still wins first.
    // VOD flow gets a proper release date instead of "today" (synced_at).
    const ytDate = video.scheduled_start || video.actual_start || video.published_at || video.synced_at
    if (ytDate && !releaseDate) {
      setReleaseDate(new Date(ytDate).toISOString().split('T')[0])
    }
    
    // Auto-populate live fields from YouTube event
    if (video.cf_live_input_id) setCfLiveInputId(video.cf_live_input_id)
    if (video.viloud_channel_name) setViloudChannelName(video.viloud_channel_name)
    if (video.viloud_channel_id) setViloudChannelId(video.viloud_channel_id)
    if (video.duration_minutes) setDurationMinutes(video.duration_minutes)
    if (video.stream_notes) setStreamNotes(video.stream_notes)
    if (video.scheduled_start) {
      // Convert ISO to datetime-local format
      const dt = new Date(video.scheduled_start)
      const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      setScheduledStart(local)
    }
    // If this is an upcoming/live YouTube event, default to live mode
    if (['upcoming', 'live'].includes(video.event_type)) {
      setEventMode('live')
      setIncludeInSchedule(true)
    }

    // Fetch and merge tags from YouTube video
    setLoadingTags(true)
    const ytTags = await fetchTagsForYtVideo(video)
    if (ytTags.length > 0) {
      setTags(prev => [...new Set([...prev, ...ytTags])])
    }
    setLoadingTags(false)
  }

  async function fetchTagsForYtVideo(ytVideo) {
    if (!ytVideo.tbtv_id) return []
    try {
      const tagData = await api(`/tags/${ytVideo.tbtv_id}`)
      return (tagData.tags || []).map(t => typeof t === 'string' ? t : t.name).filter(Boolean)
    } catch { /* no tags yet */ }
    return []
  }

  function clearYtSelection() {
    setSelectedYt(null)
    setYtSearch('')
    loadYtVideos()
  }

  // === CF Stream Functions ===
  async function loadCfStreams() {
    setCfLoading(true)
    try {
      const data = await api('/videos?per_page=100')
      const list = parseVideoList(data)
      setCfStreams(list)
    } catch (e) {
      console.error('Failed to load streams:', e)
      setCfStreams([])
    }
    setCfLoading(false)
  }

  async function searchCfStreams(query) {
    if (!query.trim()) return
    setCfLoading(true)
    try {
      const data = await api(`/videos?search=${encodeURIComponent(query)}&per_page=50`)
      const list = parseVideoList(data)
      setCfStreams(list)
    } catch (e) {
      console.error('CF search failed:', e)
    }
    setCfLoading(false)
  }

  function handleCfSearchChange(e) {
    const val = e.target.value
    setCfSearch(val)
    setShowCfDropdown(true)
    
    if (cfSearchTimerRef.current) clearTimeout(cfSearchTimerRef.current)
    if (val.trim().length >= 2) {
      cfSearchTimerRef.current = setTimeout(() => searchCfStreams(val), 400)
    } else if (val.trim().length === 0) {
      loadCfStreams()
    }
  }

  function getDisplayedCfStreams() {
    if (!cfSearch.trim()) return cfStreams
    const q = cfSearch.toLowerCase()
    return cfStreams.filter(s =>
      (s.meta?.name || '').toLowerCase().includes(q) ||
      s.uid.toLowerCase().includes(q)
    )
  }

  async function selectCfStream(stream) {
    setSelectedCf(stream)
    setCfSearch(stream.meta?.name || stream.uid)
    setShowCfDropdown(false)
    
    // Set HLS URL from CF stream
    const hls = stream.playback?.hls || ''
    setMediaUrl(hls)
    setVideoType('CF')
    
    // If no YT video or Strimm video selected, use stream title (strip any legacy "[linked]" prefix —
    // the catalog title is the plain source of truth).
    if (!selectedYt && !selectedStrim && !title) {
      setTitle((stream.meta?.name || '').replace(/^\s*\[linked\]\s*/i, ''))
    }
    
    // Set duration from stream if available
    if (stream.duration) {
      setDuration(Math.floor(stream.duration))
    }
    
    // Fetch and merge tags from CF Stream
    setLoadingTags(true)
    const cfTags = await fetchTagsForStream(stream.uid)
    if (cfTags.length > 0) {
      setTags(prev => [...new Set([...prev, ...cfTags])])
    }
    setLoadingTags(false)
  }

  async function fetchTagsForStream(streamUid) {
    try {
      const resolved = await api('/content/resolve', {
        method: 'POST',
        body: JSON.stringify({ stream_uid: streamUid })
      })
      if (resolved?.tbtv_id) {
        const tagData = await api(`/tags/${resolved.tbtv_id}`)
        return (tagData.tags || []).map(t => typeof t === 'string' ? t : t.name).filter(Boolean)
      }
    } catch { /* no content record yet */ }
    return []
  }

  function clearCfSelection() {
    setSelectedCf(null)
    setCfSearch('')
    setMediaUrl('')
    loadCfStreams()
  }

  // === Mux Stream Functions ===
  async function loadMuxAssets() {
    setMuxLoading(true)
    try {
      const data = await api('/mux/assets?limit=100')
      setMuxAssets(data.data || data || [])
    } catch (e) {
      console.error('Failed to load Mux assets:', e)
      setMuxAssets([])
    }
    setMuxLoading(false)
  }

  function getMuxPlaybackId(asset) {
    return asset?.playback_ids?.[0]?.id || null
  }

  function getMuxTitle(asset) {
    // Strip any legacy "[linked]" prefix — the catalog title is the plain source of truth.
    return (asset?.meta?.title || asset?.passthrough || asset?.id || '').replace(/^\s*\[linked\]\s*/i, '')
  }

  function getDisplayedMuxAssets() {
    if (!muxSearch.trim()) return muxAssets
    const q = muxSearch.toLowerCase()
    return muxAssets.filter(a =>
      getMuxTitle(a).toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q)
    )
  }

  function handleMuxSearchChange(e) {
    setMuxSearch(e.target.value)
    setShowMuxDropdown(true)
  }

  function selectMuxAsset(asset) {
    setSelectedMux(asset)
    setMuxSearch(getMuxTitle(asset))
    setShowMuxDropdown(false)

    const pid = getMuxPlaybackId(asset)
    if (pid) {
      setMediaUrl(`https://stream.mux.com/${pid}.m3u8`)
      setVideoType('MUX')
    }

    // Use Mux title if nothing else set
    if (!selectedYt && !selectedStrim && !selectedCf && !title) {
      setTitle(getMuxTitle(asset))
    }

    // Set duration
    if (asset.duration) {
      setDuration(Math.floor(asset.duration))
    }

    // Set thumbnail from Mux
    if (pid && !thumbnailUrl) {
      setThumbnailUrl(`https://image.mux.com/${pid}/thumbnail.jpg?width=480&height=270&time=5`)
    }
  }

  function clearMuxSelection() {
    setSelectedMux(null)
    setMuxSearch('')
    setMediaUrl('')
    loadMuxAssets()
  }

  // === Strimm Partners Functions ===
  async function loadStrimVideos(partner, date) {
    if (!partner) return
    setStrimLoading(true)
    try {
      const url = `https://iframe-api.strimm.com/api/v1/services/embed/users/${partner.id}/channels/${partner.channelId}/video?dates=${date}`
      const res = await fetch(url, { headers: { accept: 'application/json' } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setStrimVideos(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Strimm fetch failed:', e)
      setStrimVideos([])
    }
    setStrimLoading(false)
    // Reset multi-select when loading new data
    setStrimChecked(new Set())
  }

  function handleStrimPartnerChange(partnerId) {
    const p = strimPartners.find(x => x.id === partnerId)
    setStrimActivePartner(p || null)
    setSelectedStrim(null)
    setStrimSearch('')
    setStrimChecked(new Set())
    if (p) loadStrimVideos(p, strimDate)
  }

  function handleStrimDateChange(newDate) {
    setStrimDate(newDate)
    setStrimChecked(new Set())
    if (strimActivePartner) loadStrimVideos(strimActivePartner, newDate)
  }

  function getDisplayedStrimVideos() {
    if (!strimSearch.trim()) return strimVideos
    const q = strimSearch.toLowerCase()
    return strimVideos.filter(v => (v.title || '').toLowerCase().includes(q))
  }

  function selectStrimVideo(video) {
    setSelectedStrim(video)
    setStrimSearch(video.title || 'Untitled')
    setShowStrimDropdown(false)
    setStrimBatchMode(false)

    // Pre-populate fields from Strimm metadata
    const hlsUrl = video.providerVideoId || ''
    if (hlsUrl) {
      setMediaUrl(hlsUrl)
      setVideoType('HLS')
    }
    if (!title || editingTitle === false) setTitle(video.title || '')
    if (!shortDesc && video.description) setShortDesc(shortDescFrom(video.description))
    if (!longDesc && video.description) setLongDesc(video.description)

    // Set duration from Strimm
    const secs = parseStrimDuration(video.duration)
    if (secs > 0) setDuration(secs)

    // Auto-add tbtv-strimm tag
    setTags(prev => [...new Set([...prev, 'tbtv-strimm'])])
  }

  function clearStrimSelection() {
    const prevUrl = selectedStrim?.providerVideoId || ''
    setSelectedStrim(null)
    setStrimSearch('')
    if (mediaUrl === prevUrl) setMediaUrl('')
  }

  // ─── Strimm multi-select helpers ──────────────────────────
  function strimVideoKey(v, i) {
    return `${v.providerVideoId || ''}_${v.title || ''}_${i}`
  }

  function toggleStrimCheck(video, idx) {
    const key = strimVideoKey(video, idx)
    setStrimChecked(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  function toggleStrimCheckAll() {
    const displayed = getDisplayedStrimVideos()
    const allKeys = displayed.map((v, i) => strimVideoKey(v, i))
    const allChecked = allKeys.every(k => strimChecked.has(k))
    if (allChecked) {
      setStrimChecked(new Set())
    } else {
      setStrimChecked(new Set(allKeys))
    }
  }

  function getCheckedStrimVideos() {
    const displayed = getDisplayedStrimVideos()
    return displayed.filter((v, i) => strimChecked.has(strimVideoKey(v, i)))
  }

  async function handleStrimBatchImport() {
    const selected = getCheckedStrimVideos()
    if (selected.length === 0) return

    // Filter out duplicates
    const dupes = selected.filter(v => isDuplicateTitle(v.title))
    const fresh = selected.filter(v => !isDuplicateTitle(v.title))

    if (fresh.length === 0) {
      setStrimImportStatus(`All ${dupes.length} selected video(s) already exist in the catalog.`)
      return
    }

    const dupeNote = dupes.length > 0 ? `\n\n${dupes.length} duplicate(s) will be skipped.` : ''
    if (!confirm(`Import ${fresh.length} Strimm video(s) as new catalog entries?${dupeNote}\n\nEach will be created as a draft.`)) return

    setStrimImporting(true)
    setStrimImportStatus('')
    let ok = 0, fail = 0

    // Ensure tbtv-strimm is always included
    const batchTags = [...new Set(['tbtv-strimm', ...strimBatchTags])]

    for (let i = 0; i < fresh.length; i++) {
      const video = fresh[i]
      const hlsUrl = video.providerVideoId || ''
      const secs = parseStrimDuration(video.duration)
      try {
        // Step 1: Create VOD without tags to avoid D1 SQL variable limit
        const result = await api('/vod/create-linked', {
          method: 'POST',
          body: JSON.stringify({
            title: (video.title || 'Untitled Strimm').trim(),
            media_url: hlsUrl,
            video_type: 'HLS',
            quality: 'FHD',
            duration: secs,
            short_description: shortDescFrom(video.description),
            long_description: video.description || '',
            thumbnail_url: strimBatchThumb,
            release_date: strimDate || null,
            status: 'draft',
            tags: [],
            youtube_id: null,
            cf_stream_uid: null,
            strimm_source: {
              partner_id: strimActivePartner?.id || null,
              channel_id: strimActivePartner?.channelId || null,
              partner_label: strimActivePartner?.label || null,
              video_title: video.title || null,
              provider_video_id: hlsUrl || null,
            },
          })
        })

        // Step 2: Apply tags separately via PUT /tags/:tbtv_id
        if (batchTags.length > 0 && result?.tbtv_id) {
          try {
            await api(`/tags/${result.tbtv_id}`, {
              method: 'PUT',
              body: JSON.stringify({ tags: batchTags })
            })
          } catch (tagErr) {
            console.warn(`Tags failed for "${video.title}":`, tagErr)
          }
        }

        ok++
      } catch (e) {
        console.error(`Failed to import "${video.title}":`, e)
        fail++
      }
      // Update progress
      setStrimImportStatus(`Importing ${i + 1}/${fresh.length}...`)
    }

    const skipNote = dupes.length > 0 ? `, ${dupes.length} skipped as duplicates` : ''
    const msg = `✅ Imported ${ok} of ${fresh.length} Strimm video(s)${fail ? ` (${fail} failed)` : ''}${skipNote}`
    setStrimImportStatus(msg)
    setStatus(msg)
    setStrimChecked(new Set())
    setStrimImporting(false)

    // If all succeeded, trigger parent refresh
    if (ok > 0) onComplete()
  }

  // === Tag Management ===
  function addTag(tagName) {
    const clean = tagName.trim().toLowerCase()
    if (!clean || tags.includes(clean)) return
    setTags(prev => [...prev, clean])
    setTagInput('')
    setShowTagSuggestions(false)
  }

  function removeTag(tagName) {
    setTags(prev => prev.filter(t => t !== tagName))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  const combinedTags = [...new Set([...allTags, ...tags])]
  const filteredSuggestions = tagInput.length > 0
    ? combinedTags.filter(t => t.includes(tagInput.toLowerCase()) && !tags.includes(t))
    : []

  // === Submit (single item) ===
  async function handleSubmit(e) {
    e.preventDefault()
    
    if (!title.trim()) {
      setStatus('Error: Title is required')
      return
    }
    // Live events don't require media_url (they use CF Live Input HLS)
    if (eventMode !== 'live' && !mediaUrl.trim() && !selectedCf) {
      setStatus('Error: Select a Cloudflare Stream video, Strimm video, or enter a Media URL')
      return
    }
    if (eventMode === 'live' && !scheduledStart) {
      setStatus('Error: Scheduled Start is required for live events')
      return
    }
    if (isDuplicateTitle(title) && !confirm(`"${title.trim()}" already exists in the catalog.\n\nCreate a duplicate entry anyway?`)) {
      return
    }

    setSaving(true)
    try {
      // Convert datetime-local to ISO for live events
      const isoScheduledStart = scheduledStart ? new Date(scheduledStart).toISOString() : null

      // Use the unified VOD creation endpoint that handles linkage
      await api('/vod/create-linked', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          media_url: mediaUrl.trim(),
          video_type: videoType,
          quality,
          duration: parseInt(duration) || 0,
          short_description: shortDesc,
          long_description: longDesc,
          thumbnail_url: thumbnailUrl,
          release_date: releaseDate || null,
          status: itemStatus,
          tags,
          // Linkage fields
          youtube_id: selectedYt?.youtube_id || null,
          cf_stream_uid: selectedCf?.uid || null,
          mux_asset_id: selectedMux?.id || null,
          // Strimm source tracking
          strimm_source: selectedStrim ? {
            partner_id: strimActivePartner?.id || null,
            channel_id: strimActivePartner?.channelId || null,
            partner_label: strimActivePartner?.label || null,
            video_title: selectedStrim.title || null,
            provider_video_id: selectedStrim.providerVideoId || null,
          } : null,
          // Unified content library: live-event fields
          event_mode: eventMode,
          scheduled_start: isoScheduledStart,
          cf_live_input_id: cfLiveInputId || null,
          viloud_channel_name: viloudChannelName || null,
          viloud_channel_id: viloudChannelId || null,
          stream_notes: streamNotes || null,
          duration_minutes: parseInt(durationMinutes) || 120,
          include_in_schedule: eventMode === 'live' && includeInSchedule ? 1 : 0,
        })
      })
      setStatus(eventMode === 'live' ? '✅ Live event created' : '✅ Content created and linked')
      onComplete()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  function formatDuration(secs) {
    if (!secs || secs <= 0) return '—'
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const displayedYt = getDisplayedYtVideos()
  const displayedCf = getDisplayedCfStreams()
  const displayedMux = getDisplayedMuxAssets()
  const displayedStrim = getDisplayedStrimVideos()
  const checkedStrimCount = strimChecked.size

  return (
    <div className="catalog-create">
      <div className="create-header">
        <h2><Link size={18} /> Add New Content</h2>
        <button className="btn-ghost" onClick={onCancel}>
          <X size={16} /> Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Event Mode Toggle */}
        <div className="detail-section">
          <h3>Event Mode</h3>
          <div className="event-mode-toggle">
            <button
              type="button"
              className={`mode-btn ${eventMode === 'ondemand' ? 'active' : ''}`}
              onClick={() => setEventMode('ondemand')}
            >
              <Play size={14} /> On-Demand
            </button>
            <button
              type="button"
              className={`mode-btn mode-btn-live ${eventMode === 'live' ? 'active' : ''}`}
              onClick={() => { setEventMode('live'); setIncludeInSchedule(true) }}
            >
              <Radio size={14} /> Live Event
            </button>
            <button
              type="button"
              className={`mode-btn ${eventMode === 'highlights' ? 'active' : ''}`}
              style={eventMode === 'highlights'
                ? { background: '#facc15', color: '#000', borderColor: '#facc15' }
                : { borderColor: '#facc15', color: '#facc15' }}
              onClick={() => setEventMode('highlights')}
            >
              <Film size={14} /> Highlights
            </button>
          </div>
          {eventMode === 'live' && (
            <p className="section-hint">This event will appear in the live schedule when published.</p>
          )}
          {eventMode === 'highlights' && (
            <p className="section-hint">Publish an existing highlight reel as a content-catalog item. Pick a reel below; descriptions, thumbnail, and duration auto-fill from its dominant source.</p>
          )}
        </div>

        {/* Highlights mode: reel picker replaces the standard source pickers below */}
        {eventMode === 'highlights' && (
          <HighlightsPublishSection
            api={api}
            onPublished={(tbtvId) => {
              if (typeof onComplete === 'function') onComplete(tbtvId)
              if (typeof onCancel === 'function') onCancel()
            }}
          />
        )}

        {/* Standard form fields — hidden when Highlights mode is active */}
        {eventMode !== 'highlights' && (<>
        {/* Live Event Fields (only shown in live mode) */}
        {eventMode === 'live' && (
          <div className="detail-section">
            <h3><Radio size={16} /> Live Event Settings</h3>

            <div className="form-row">
              <label>Scheduled Start *</label>
              <input
                type="datetime-local"
                value={scheduledStart}
                onChange={e => setScheduledStart(e.target.value)}
                required={eventMode === 'live'}
              />
            </div>

            <div className="form-row-grid">
              <div className="form-row">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={e => setDurationMinutes(e.target.value)}
                  min="1"
                />
              </div>
              <div className="form-row">
                <label>TBTV Channel</label>
                <select
                  value={viloudChannelId}
                  onChange={e => handleTbtvChannelSelect(e.target.value)}
                >
                  <option value="">— Select Channel —</option>
                  {tbtvChannels.map(ch => (
                    <option key={ch.viloud_id || ch.tbtv_channel_id} value={ch.viloud_id}>
                      {ch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <label>CF Live Input</label>
              <select
                value={cfLiveInputId}
                onChange={e => setCfLiveInputId(e.target.value)}
              >
                <option value="">— Select Live Input —</option>
                {liveInputs.map(li => (
                  <option key={li.uid} value={li.uid}>
                    {li.meta?.name || li.uid}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label>Stream Notes</label>
              <textarea
                value={streamNotes}
                onChange={e => setStreamNotes(e.target.value)}
                placeholder="Internal notes for this stream..."
                rows={2}
              />
            </div>

            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeInSchedule}
                  onChange={e => setIncludeInSchedule(e.target.checked)}
                />
                Include in Live Schedule
              </label>
            </div>
          </div>
        )}

        {/* Source Linking Section */}
        <div className="detail-section">
          <h3><Youtube size={16} /> Link YouTube Archive (Optional)</h3>
          <p className="section-hint">Select a YouTube video to use its metadata and mark it as linked</p>
          
          <div className="source-dropdown" ref={ytDropdownRef}>
            <div className="source-input-wrap">
              <input
                type="text"
                placeholder={ytLoading ? 'Loading videos...' : 'Search YouTube archive videos...'}
                value={ytSearch}
                onChange={handleYtSearchChange}
                onFocus={() => setShowYtDropdown(true)}
              />
              <button
                type="button"
                className="dropdown-toggle"
                onClick={() => setShowYtDropdown(!showYtDropdown)}
              >
                <ChevronDown size={14} className={showYtDropdown ? 'rotated' : ''} />
              </button>
            </div>
            {showYtDropdown && (
              <div className="source-dropdown-list">
                {ytLoading ? (
                  <div className="dropdown-loading">Loading...</div>
                ) : displayedYt.length === 0 ? (
                  <div className="dropdown-empty">
                    {ytSearch ? 'No videos match your search' : 'No unlinked videos found'}
                  </div>
                ) : (
                  displayedYt.slice(0, 50).map(v => (
                    <div
                      key={v.youtube_id}
                      className={`dropdown-item ${v.youtube_id === selectedYt?.youtube_id ? 'selected' : ''}`}
                      onClick={() => selectYtVideo(v)}
                    >
                      {v.thumbnail_url && <img src={v.thumbnail_url} alt="" />}
                      <div className="dropdown-item-info">
                        <div className="dropdown-item-name">{v.title || v.youtube_id}</div>
                        <div className="dropdown-item-meta">
                          {v.channel_name} · {v.youtube_id}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {selectedYt && (
            <div className="source-selected">
              <Youtube size={14} />
              <span>Selected: <strong>{selectedYt.title || selectedYt.youtube_id}</strong></span>
              <code>{selectedYt.youtube_id}</code>
              <button type="button" className="btn-ghost btn-sm" onClick={clearYtSelection}>
                <X size={12} /> Clear
              </button>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3><Video size={16} /> Link Cloudflare Stream (Optional)</h3>
          <p className="section-hint">Select a stream video to use its HLS URL and mark it as linked</p>
          
          <div className="source-dropdown" ref={cfDropdownRef}>
            <div className="source-input-wrap">
              <input
                type="text"
                placeholder={cfLoading ? 'Loading videos...' : 'Search Cloudflare Stream videos...'}
                value={cfSearch}
                onChange={handleCfSearchChange}
                onFocus={() => setShowCfDropdown(true)}
              />
              <button
                type="button"
                className="dropdown-toggle"
                onClick={() => setShowCfDropdown(!showCfDropdown)}
              >
                <ChevronDown size={14} className={showCfDropdown ? 'rotated' : ''} />
              </button>
            </div>
            {showCfDropdown && (
              <div className="source-dropdown-list">
                {cfLoading ? (
                  <div className="dropdown-loading">Loading...</div>
                ) : displayedCf.length === 0 ? (
                  <div className="dropdown-empty">
                    {cfSearch ? 'No videos match your search' : 'No videos found'}
                  </div>
                ) : (
                  displayedCf.slice(0, 50).map(s => (
                    <div
                      key={s.uid}
                      className={`dropdown-item ${s.uid === selectedCf?.uid ? 'selected' : ''}`}
                      onClick={() => selectCfStream(s)}
                    >
                      {s.thumbnail && <img src={s.thumbnail} alt="" />}
                      <div className="dropdown-item-info">
                        <div className="dropdown-item-name">{s.meta?.name || s.uid}</div>
                        <div className="dropdown-item-meta">
                          {new Date(s.created).toLocaleDateString()} · {s.uid.slice(0, 12)}...
                          {s.duration > 0 && <span> · {Math.floor(s.duration / 60)}m{Math.floor(s.duration % 60)}s</span>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {selectedCf && (
            <div className="source-selected cf">
              <Video size={14} />
              <span>Selected: <strong>{selectedCf.meta?.name || selectedCf.uid}</strong></span>
              <code>{selectedCf.uid.slice(0, 16)}...</code>
              <button type="button" className="btn-ghost btn-sm" onClick={clearCfSelection}>
                <X size={12} /> Clear
              </button>
            </div>
          )}

          {/* Manual HLS URL input */}
          <div className="form-row" style={{ marginTop: '12px' }}>
            <label>Or Enter HLS URL Directly</label>
            <input
              type="text"
              value={mediaUrl}
              onChange={e => setMediaUrl(e.target.value)}
              placeholder="https://..."
            />
            {selectedCf && mediaUrl && (
              <div className="field-note">
                <AlertCircle size={12} /> HLS auto-populated from Cloudflare Stream
              </div>
            )}
            {selectedStrim && mediaUrl && !selectedCf && (
              <div className="field-note strimm-note">
                <Tv size={12} /> HLS auto-populated from Strimm Partners
              </div>
            )}
          </div>
        </div>

        {/* ─── Mux Stream Source ─── */}
        <div className="detail-section">
          <h3><Video size={16} /> Link Mux Stream (Optional)</h3>
          <p className="section-hint">Select a Mux video asset to use its HLS URL</p>

          <div className="source-dropdown" ref={muxDropdownRef}>
            <div className="source-input-wrap">
              <input
                type="text"
                placeholder={muxLoading ? 'Loading Mux assets...' : 'Search Mux video assets...'}
                value={muxSearch}
                onChange={handleMuxSearchChange}
                onFocus={() => { setShowMuxDropdown(true); if (!muxAssets.length) loadMuxAssets() }}
              />
              <button
                type="button"
                className="dropdown-toggle"
                onClick={() => setShowMuxDropdown(!showMuxDropdown)}
              >
                <ChevronDown size={14} className={showMuxDropdown ? 'rotated' : ''} />
              </button>
            </div>
            {showMuxDropdown && (
              <div className="source-dropdown-list">
                {muxLoading ? (
                  <div className="dropdown-loading">Loading...</div>
                ) : displayedMux.length === 0 ? (
                  <div className="dropdown-empty">
                    {muxSearch ? 'No assets match your search' : 'No Mux assets found'}
                  </div>
                ) : (
                  displayedMux.slice(0, 50).map(a => {
                    const pid = getMuxPlaybackId(a)
                    return (
                      <div
                        key={a.id}
                        className={`dropdown-item ${a.id === selectedMux?.id ? 'selected' : ''}`}
                        onClick={() => selectMuxAsset(a)}
                      >
                        {pid && <img src={`https://image.mux.com/${pid}/thumbnail.jpg?width=120&height=68&time=5`} alt="" />}
                        <div className="dropdown-item-info">
                          <div className="dropdown-item-name">{getMuxTitle(a)}</div>
                          <div className="dropdown-item-meta">
                            {formatDuration(a.duration)} · {a.id.slice(0, 12)}...
                            {a.status && <span> · {a.status}</span>}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
          {selectedMux && (
            <div className="source-selected cf">
              <Video size={14} />
              <span>Selected: <strong>{getMuxTitle(selectedMux)}</strong></span>
              <code>{selectedMux.id.slice(0, 16)}...</code>
              <button type="button" className="btn-ghost btn-sm" onClick={clearMuxSelection}>
                <X size={12} /> Clear
              </button>
            </div>
          )}
          {selectedMux && mediaUrl && (
            <div className="field-note" style={{ marginTop: 8 }}>
              <AlertCircle size={12} /> HLS auto-populated from Mux Stream
            </div>
          )}
        </div>

        {/* ─── Strimm Partners Source ─── */}
        <div className="detail-section">
          <h3><Tv size={16} /> Link Strimm Partner Content (Optional)</h3>
          <p className="section-hint">
            Browse partner HLS content libraries via Strimm. Select one to populate the form, or use batch mode to import multiple at once.
            {strimPartners.length === 0 && <> Configure partners in Sources → Strimm Partners first.</>}
          </p>

          {strimPartners.length > 0 && (
            <>
              {/* Partner + date selectors */}
              <div className="strimm-controls">
                <select
                  value={strimActivePartner?.id || ''}
                  onChange={e => handleStrimPartnerChange(e.target.value)}
                >
                  <option value="">Select partner…</option>
                  {strimPartners.map(p => (
                    <option key={p.id} value={p.id}>{p.label} ({p.channelId})</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={strimDate}
                  onChange={e => handleStrimDateChange(e.target.value)}
                  className="strimm-date"
                />
                {strimActivePartner && (
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => loadStrimVideos(strimActivePartner, strimDate)}
                    disabled={strimLoading}
                  >
                    {strimLoading ? 'Loading…' : `${strimVideos.length} videos`}
                  </button>
                )}
              </div>

              {/* Mode toggle: single select vs batch import */}
              {strimActivePartner && strimVideos.length > 0 && (
                <div className="strimm-mode-toggle">
                  <button
                    type="button"
                    className={`btn-ghost btn-sm ${!strimBatchMode ? 'active' : ''}`}
                    onClick={() => { setStrimBatchMode(false); setStrimChecked(new Set()) }}
                  >
                    Single Select
                  </button>
                  <button
                    type="button"
                    className={`btn-ghost btn-sm ${strimBatchMode ? 'active' : ''}`}
                    onClick={() => { setStrimBatchMode(true); setSelectedStrim(null); setStrimSearch('') }}
                  >
                    <Upload size={12} /> Batch Import
                  </button>
                </div>
              )}

              {/* Strimm video dropdown — single select mode */}
              {strimActivePartner && !strimBatchMode && (
                <div className="source-dropdown" ref={strimDropdownRef}>
                  <div className="source-input-wrap">
                    <input
                      type="text"
                      placeholder={strimLoading ? 'Loading Strimm videos...' : `Search ${strimVideos.length} videos from ${strimActivePartner.label}...`}
                      value={strimSearch}
                      onChange={e => { setStrimSearch(e.target.value); setShowStrimDropdown(true) }}
                      onFocus={() => setShowStrimDropdown(true)}
                    />
                    <button
                      type="button"
                      className="dropdown-toggle"
                      onClick={() => setShowStrimDropdown(!showStrimDropdown)}
                    >
                      <ChevronDown size={14} className={showStrimDropdown ? 'rotated' : ''} />
                    </button>
                  </div>
                  {showStrimDropdown && (
                    <div className="source-dropdown-list">
                      {strimLoading ? (
                        <div className="dropdown-loading">Loading...</div>
                      ) : displayedStrim.length === 0 ? (
                        <div className="dropdown-empty">
                          {strimSearch ? 'No videos match your search' : 'No videos found for this date'}
                        </div>
                      ) : (
                        displayedStrim.slice(0, 50).map((v, i) => {
                          const isHls = (v.providerVideoId || '').includes('.m3u8')
                          const isDupe = isDuplicateTitle(v.title)
                          return (
                            <div
                              key={`${v.providerVideoId}-${i}`}
                              className={`dropdown-item ${selectedStrim?.providerVideoId === v.providerVideoId && selectedStrim?.title === v.title ? 'selected' : ''} ${isDupe ? 'dupe' : ''}`}
                              onClick={() => selectStrimVideo(v)}
                            >
                              <div className="dropdown-item-info">
                                <div className="dropdown-item-name">
                                  {v.title || 'Untitled'}
                                  {isHls && <span className="strimm-hls-badge">HLS</span>}
                                  {isDupe && <span className="strimm-dupe-badge">EXISTS</span>}
                                </div>
                                <div className="dropdown-item-meta">
                                  {v.duration || '—'} · ch:{v.channelId}
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Strimm batch import mode — checkbox list */}
              {strimActivePartner && strimBatchMode && (
                <div className="strimm-batch-panel" ref={strimDropdownRef}>
                  <div className="strimm-batch-header">
                    <div className="strimm-batch-search">
                      <input
                        type="text"
                        placeholder={`Filter ${strimVideos.length} videos...`}
                        value={strimSearch}
                        onChange={e => setStrimSearch(e.target.value)}
                      />
                    </div>
                    <button type="button" className="btn-ghost btn-sm" onClick={toggleStrimCheckAll}>
                      {displayedStrim.length > 0 && displayedStrim.every((v, i) => strimChecked.has(strimVideoKey(v, i)))
                        ? <><CheckSquare size={12} /> Deselect All</>
                        : <><Square size={12} /> Select All ({displayedStrim.length})</>
                      }
                    </button>
                  </div>
                  <div className="strimm-batch-list">
                    {strimLoading ? (
                      <div className="dropdown-loading">Loading...</div>
                    ) : displayedStrim.length === 0 ? (
                      <div className="dropdown-empty">
                        {strimSearch ? 'No videos match your filter' : 'No videos found for this date'}
                      </div>
                    ) : (
                      displayedStrim.map((v, i) => {
                        const key = strimVideoKey(v, i)
                        const isChecked = strimChecked.has(key)
                        const isHls = (v.providerVideoId || '').includes('.m3u8')
                        const isDupe = isDuplicateTitle(v.title)
                        const durSecs = parseStrimDuration(v.duration)
                        return (
                          <div
                            key={key}
                            className={`strimm-batch-item ${isChecked ? 'checked' : ''} ${isDupe ? 'dupe' : ''}`}
                            onClick={() => toggleStrimCheck(v, i)}
                          >
                            <span className="strimm-batch-check">
                              {isChecked ? <CheckSquare size={14} /> : <Square size={14} />}
                            </span>
                            <div className="strimm-batch-info">
                              <div className="strimm-batch-title">
                                {v.title || 'Untitled'}
                                {isHls && <span className="strimm-hls-badge">HLS</span>}
                                {isDupe && <span className="strimm-dupe-badge">EXISTS</span>}
                              </div>
                              <div className="strimm-batch-meta">
                                {v.duration || '—'}
                                {durSecs > 0 && <span> ({formatDuration(durSecs)})</span>}
                                {' · '}ch:{v.channelId}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>

                  {/* Batch defaults + action bar */}
                  {checkedStrimCount > 0 && (
                    <div className="strimm-batch-defaults">
                      <div className="strimm-batch-default-row">
                        <label>Default Thumbnail:</label>
                        <ImagePicker
                          value={strimBatchThumb}
                          onChange={setStrimBatchThumb}
                          placeholder="Browse or paste thumbnail URL for all imports..."
                        />
                      </div>
                      <div className="strimm-batch-default-row">
                        <label>Default Tags:</label>
                        <div className="strimm-batch-tag-wrap">
                          <div className="strimm-batch-tag-chips">
                            {strimBatchTags.map(t => (
                              <span key={t} className="tag-chip">
                                {t}
                                {t !== 'tbtv-strimm' && (
                                  <button type="button" className="tag-remove" onClick={() => setStrimBatchTags(prev => prev.filter(x => x !== t))}>
                                    <X size={10} />
                                  </button>
                                )}
                              </span>
                            ))}
                            <input
                              type="text"
                              className="tag-text-input"
                              value={strimBatchTagInput}
                              onChange={e => setStrimBatchTagInput(e.target.value)}
                              onKeyDown={e => {
                                if ((e.key === 'Enter' || e.key === ',') && strimBatchTagInput.trim()) {
                                  e.preventDefault()
                                  const t = strimBatchTagInput.trim().toLowerCase()
                                  if (!strimBatchTags.includes(t)) setStrimBatchTags(prev => [...prev, t])
                                  setStrimBatchTagInput('')
                                }
                              }}
                              placeholder="Add tag..."
                            />
                          </div>
                        </div>
                      </div>
                      <div className="strimm-batch-actions">
                        <span className="strimm-batch-count">
                          <CheckSquare size={12} /> {checkedStrimCount} selected
                        </span>
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={handleStrimBatchImport}
                          disabled={strimImporting}
                        >
                          <Upload size={12} />
                          {strimImporting ? 'Importing…' : `Import ${checkedStrimCount} as Draft`}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => setStrimChecked(new Set())}
                        >
                          <X size={12} /> Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {strimImportStatus && (
                    <div className="strimm-batch-status">{strimImportStatus}</div>
                  )}
                </div>
              )}

              {/* Single selection banner */}
              {selectedStrim && !strimBatchMode && (
                <div className="source-selected strimm">
                  <Tv size={14} />
                  <span>Selected: <strong>{selectedStrim.title || 'Untitled'}</strong></span>
                  {selectedStrim.duration && <code>{selectedStrim.duration} ({formatDuration(parseStrimDuration(selectedStrim.duration))})</code>}
                  <button type="button" className="btn-ghost btn-sm" onClick={clearStrimSelection}>
                    <X size={12} /> Clear
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Content Information */}
        <div className="detail-section">
          <h3>{eventMode === 'live' ? 'Event Information' : 'VOD Information'}</h3>
          
          <div className="form-row">
            <label>
              Title *
              <button type="button" className="btn-edit" onClick={() => setEditingTitle(!editingTitle)}>
                <Edit3 size={10} /> {editingTitle ? 'Done' : 'Edit'}
              </button>
            </label>
            {editingTitle || (!selectedYt && !selectedStrim) ? (
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Video title"
                required
              />
            ) : (
              <div className="preview-value">{title || '(no title)'}</div>
            )}
            {(selectedYt || selectedStrim) && (
              <div className="field-note">Pre-populated from {selectedYt ? 'YouTube' : 'Strimm'}. Click Edit to modify.</div>
            )}
          </div>

          <div className="form-row-grid">
            <div className="form-row">
              <label>Video Type</label>
              <select value={videoType} onChange={e => handleVideoTypeChange(e.target.value)}>
                <option value="MUX">MUX</option>
                <option value="CF">CF</option>
                <option value="MP4">MP4</option>
                <option value="HLS">HLS (legacy)</option>
                <option value="YT">YouTube (web only)</option>
              </select>
            </div>
            <div className="form-row">
              <label>Quality</label>
              <select value={quality} onChange={e => setQuality(e.target.value)}>
                <option value="SD">SD (480p)</option>
                <option value="HD">HD (720p)</option>
                <option value="FHD">FHD (1080p)</option>
                <option value="UHD">UHD (4K)</option>
              </select>
            </div>
          </div>

          <div className="form-row-grid">
            <div className="form-row">
              <label>Duration (seconds)</label>
              <div className="duration-input">
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  min="0"
                />
                <span className="duration-display">{formatDuration(parseInt(duration) || 0)}</span>
              </div>
            </div>
            <div className="form-row">
              <label>
                Release Date
                <button type="button" className="btn-edit" onClick={() => setEditingDate(!editingDate)}>
                  <Edit3 size={10} /> {editingDate ? 'Done' : 'Edit'}
                </button>
              </label>
              {editingDate || (!selectedYt && !selectedStrim) ? (
                <input
                  type="date"
                  value={releaseDate}
                  onChange={e => setReleaseDate(e.target.value)}
                />
              ) : (
                <div className="preview-value">
                  {releaseDate ? new Date(releaseDate + 'T00:00:00').toLocaleDateString() : '(no date)'}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <label>Thumbnail URL</label>
            <ImagePicker
              value={thumbnailUrl}
              onChange={setThumbnailUrl}
              placeholder="Paste URL or browse Cloudflare Images..."
            />
          </div>

          <div className="form-row">
            <label>
              Short Description
              <button type="button" className="btn-edit" onClick={() => setEditingShortDesc(!editingShortDesc)}>
                <Edit3 size={10} /> {editingShortDesc ? 'Done' : 'Edit'}
              </button>
            </label>
            {editingShortDesc || (!selectedYt && !selectedStrim) ? (
              <textarea
                value={shortDesc}
                onChange={e => setShortDesc(e.target.value)}
                placeholder="Brief description for listings (first 200 chars)"
                rows={2}
              />
            ) : (
              <div className="preview-value">{shortDesc || '(no description)'}</div>
            )}
          </div>

          <div className="form-row">
            <label>
              Long Description
              <button type="button" className="btn-edit" onClick={() => setEditingLongDesc(!editingLongDesc)}>
                <Edit3 size={10} /> {editingLongDesc ? 'Done' : 'Edit'}
              </button>
            </label>
            {editingLongDesc || (!selectedYt && !selectedStrim) ? (
              <textarea
                value={longDesc}
                onChange={e => setLongDesc(e.target.value)}
                placeholder="Detailed description"
                rows={4}
              />
            ) : (
              <div className="preview-value" style={{ maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                {longDesc || '(no description)'}
              </div>
            )}
          </div>

          <div className="form-row">
            <label>Initial Status</label>
            <select value={itemStatus} onChange={e => setItemStatus(e.target.value)}>
              <option value="draft">Draft (not published)</option>
              <option value="active">Active (published)</option>
            </select>
          </div>
        </div>

        {/* Tags */}
        <div className="detail-section">
          <h3><Tag size={16} /> Tags {loadingTags && <span className="loading-hint">(loading...)</span>}</h3>
          {(selectedYt || selectedCf) && tags.length > 0 && (
            <p className="section-hint">Tags merged from selected sources</p>
          )}
          
          <div className="tags-input-wrap">
            <div className="tags-chip-list">
              {tags.map(tag => (
                <span key={tag} className="tag-chip">
                  {tag}
                  <button type="button" className="tag-remove" onClick={() => removeTag(tag)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="tag-text-input"
                value={tagInput}
                onChange={e => {
                  setTagInput(e.target.value)
                  setShowTagSuggestions(true)
                }}
                onKeyDown={handleTagKeyDown}
                onFocus={() => setShowTagSuggestions(true)}
                onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                placeholder={tags.length === 0 ? 'Add tags...' : ''}
              />
            </div>
            {showTagSuggestions && filteredSuggestions.length > 0 && (
              <div className="tag-suggestions">
                {filteredSuggestions.map(s => (
                  <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        {(selectedYt || selectedCf || selectedStrim || eventMode === 'live') && (
          <div className={`link-summary ${eventMode === 'live' ? 'link-summary-live' : ''}`}>
            <AlertCircle size={14} />
            <span>
              This will create {eventMode === 'live' ? 'a live event' : 'a VOD catalog entry'}
              {selectedYt && <> and link <strong>YouTube: {selectedYt.youtube_id}</strong></>}
              {selectedYt && (selectedCf || selectedStrim) && ' and'}
              {selectedCf && <> link <strong>Stream: {selectedCf.uid.slice(0, 12)}...</strong></>}
              {selectedCf && selectedStrim && ' and'}
              {selectedStrim && <> use HLS from <strong>Strimm: {(selectedStrim.title || 'Untitled').substring(0, 30)}</strong> ({formatDuration(parseStrimDuration(selectedStrim.duration))})</>}
              {selectedCf && !selectedStrim && '. The Cloudflare Stream title will be updated to match.'}
              {eventMode === 'live' && includeInSchedule && '. It will appear in the live schedule.'}
            </span>
          </div>
        )}

        </>)}

        <div className="create-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          {eventMode !== 'highlights' && (
            <button type="submit" className="btn-primary" disabled={saving}>
              <Save size={14} /> {saving ? 'Creating...' : 'Create Content'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// Step 32: Highlights publish flow inside CatalogCreate. Lets the editor
// pick an unpublished, ready reel and customize the prefilled fields
// (title, descriptions, release_date, tags, status) before submitting to
// /vod/create-from-reel. The backend handles the actual content + vod_catalog
// inserts + reverse-link to highlight_reels.tbtv_id.
function HighlightsPublishSection({ api, onPublished }) {
  const [reels, setReels] = useState([])
  const [loadingReels, setLoadingReels] = useState(true)
  const [reelsErr, setReelsErr] = useState(null)
  const [reelId, setReelId] = useState('')
  const [info, setInfo] = useState(null)
  const [loadingInfo, setLoadingInfo] = useState(false)
  const [infoErr, setInfoErr] = useState(null)
  const [title, setTitle] = useState('')
  const [shortDesc, setShortDesc] = useState('')
  const [longDesc, setLongDesc] = useState('')
  const [tagsInput, setTagsInput] = useState('tbtv-highlights')
  const [releaseDate, setReleaseDate] = useState('')
  const [status, setStatus] = useState('draft')
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoadingReels(true)
    setReelsErr(null)
    api('/highlights/reels?status=ready,preparing&limit=200')
      .then(r => {
        if (cancelled) return
        // Only show unpublished ready reels (those without a tbtv_id).
        const available = (r.reels || []).filter(r => !r.tbtv_id && r.status === 'ready')
        setReels(available)
      })
      .catch(e => !cancelled && setReelsErr(e.message))
      .finally(() => !cancelled && setLoadingReels(false))
    return () => { cancelled = true }
  }, [api])

  useEffect(() => {
    if (!reelId) { setInfo(null); return }
    let cancelled = false
    setLoadingInfo(true)
    setInfoErr(null)
    api(`/highlights/reels/${reelId}/publish-info`)
      .then(pi => {
        if (cancelled) return
        setInfo(pi)
        setTitle(`HIGHLIGHTS: ${pi.title || ''}`.slice(0, 240))
        setShortDesc(pi.short_description || '')
        setLongDesc(pi.long_description || '')
        setReleaseDate(pi.release_date || '')
      })
      .catch(e => !cancelled && setInfoErr(e.message))
      .finally(() => !cancelled && setLoadingInfo(false))
    return () => { cancelled = true }
  }, [reelId, api])

  async function submit() {
    if (!reelId) return
    setSubmitting(true)
    setSubmitErr(null)
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
      const r = await api('/vod/create-from-reel', {
        method: 'POST',
        body: JSON.stringify({
          reel_id: reelId,
          title_override: title,
          short_description_override: shortDesc,
          long_description_override: longDesc,
          tags,
          release_date: releaseDate || null,
          status,
        }),
      })
      onPublished(r.tbtv_id)
    } catch (e) {
      setSubmitErr(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="detail-section" style={{ borderLeft: '3px solid #facc15', paddingLeft: 14 }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Film size={16} /> Pick a Reel to Publish
      </h3>
      {loadingReels && <div style={{ color: '#888', fontSize: 13 }}><Loader2 size={14} className="spin" /> Loading reels…</div>}
      {reelsErr && <div style={{ color: '#dc2626', fontSize: 13 }}>Failed to load reels: {reelsErr}</div>}
      {!loadingReels && !reelsErr && (
        <>
          {reels.length === 0 ? (
            <div style={{ color: '#facc15', fontSize: 13, padding: 10, background: '#451a03', borderRadius: 4 }}>
              No unpublished reels available. Build a reel first in Highlights → Reels.
            </div>
          ) : (
            <div className="form-row">
              <label>Reel ({reels.length} available)</label>
              <select value={reelId} onChange={e => setReelId(e.target.value)}>
                <option value="">— pick a reel —</option>
                {reels.map(r => (
                  <option key={r.reel_id} value={r.reel_id}>
                    {r.title || '(untitled)'} · {r.reel_id}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loadingInfo && <div style={{ color: '#888', fontSize: 13 }}><Loader2 size={14} className="spin" /> Loading publish info…</div>}
          {infoErr && <div style={{ color: '#dc2626', fontSize: 13 }}>{infoErr}</div>}

          {info && (
            <>
              <div style={{ fontSize: 11, color: '#777', marginBottom: 12, fontFamily: 'monospace' }}>
                {info.reel_id} · {info.duration_seconds}s · {info.source_tbtv_ids?.length || 0} source video(s)
                {info.dominant_source_tbtv_id && ` · dominant: ${info.dominant_source_tbtv_id}`}
              </div>

              <div className="form-row">
                <label>Title</label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
              </div>

              <div className="form-row">
                <label>Short description <span style={{ color: '#666', fontSize: 11 }}>({shortDesc.length} chars · from TBTV-AI summary)</span></label>
                <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)} rows={2} />
              </div>

              <div className="form-row">
                <label>Long description <span style={{ color: '#666', fontSize: 11 }}>(from Mux summary)</span></label>
                <textarea value={longDesc} onChange={e => setLongDesc(e.target.value)} rows={4} />
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label>Release date</label>
                  <input type="date" value={releaseDate || ''} onChange={e => setReleaseDate(e.target.value)} />
                </div>
                <div>
                  <label>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="active">Active (publish to watch)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label>Tags <span style={{ color: '#666', fontSize: 11 }}>(comma-separated)</span></label>
                <input type="text" value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                  placeholder="tbtv-highlights, pihl, championship" />
              </div>

              {submitErr && <div style={{ color: '#dc2626', fontSize: 13, padding: 10, background: '#7f1d1d', borderRadius: 4, marginBottom: 8 }}>Publish failed: {submitErr}</div>}

              <button type="button" onClick={submit} disabled={submitting || !title.trim()}
                style={{ padding: '10px 16px', background: '#facc15', border: 'none', borderRadius: 4, color: '#000', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {submitting ? <Loader2 size={14} className="spin" /> : <ClipboardList size={14} />}
                Publish Highlights to Catalog
              </button>
            </>
          )}
        </>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  )
}

